'use es6';

import { Map as ImmutableMap, Set as ImmutableSet, List } from 'immutable';
import { isTimeSeries, isTable, getMetricField, getBucketField, getBreakdownField, getCombinedValues } from '../lib/salesAnalytics/helpers';
const mapQuotaToBucketProperty = (quotas, bucketProperty) => quotas.reduce((acc, qPoint) => acc.set(qPoint.get(bucketProperty), qPoint), ImmutableMap());

// Run secondary sort to follow rollup order for manual forecast.
const applyRollupDisplayOrder = (sortedQuota, responseBreakdown, metricField, bucketField) => {
  const sortedBucketsList = sortedQuota.map(q => q.get(bucketField));
  const sortedBreakdowns = responseBreakdown.sort((a, b) => {
    if (a.get(bucketField) === b.get(bucketField)) {
      if (a.get('displayOrder')) {
        return a.get('displayOrder') - b.get('displayOrder');
      }
      if (a.get('dealstage.probability')) {
        return a.get('dealstage.probability') - b.get('dealstage.probability');
      }
      return b.get(metricField) - a.get(metricField);
    }
    return sortedBucketsList.indexOf(a.get(bucketField)) - sortedBucketsList.indexOf(b.get(bucketField));
  });
  return sortedBreakdowns;
};
export const sortData = ({
  report,
  response: data
}) => {
  let quota = data.get('quotas');
  const bucketField = getBucketField(report);
  const metricField = `SUM|${getMetricField(report)}`;
  if (isTimeSeries(report)) {
    quota = quota.sortBy(q => q.get(bucketField));
  } else {
    quota = quota.sort((a, b) => {
      return Number(b.get(metricField)) > Number(a.get(metricField)) ? 1 : -1;
    });
  }
  const sortedBreakdowns = applyRollupDisplayOrder(quota, data.get('breakdowns'), metricField, bucketField);
  return {
    report,
    response: data.set('quotas', quota).set('breakdowns', sortedBreakdowns)
  };
};
export const addAttainmentPercentage = ({
  report,
  response: data
}) => {
  const bucketProperty = getBucketField(report);
  const amountField = getMetricField(report);
  const quotas = data.get('quotas');
  const quotaMap = mapQuotaToBucketProperty(quotas, bucketProperty);
  const breakdownsWithAttainment = data.get('breakdowns').map(dataPoint => {
    const bucketQuota = quotaMap.getIn([dataPoint.get(bucketProperty), 'quotaAmount'], 0);
    const amount = dataPoint.get(`SUM|${amountField}`);
    const attainmentPercentage = amount && bucketQuota ? amount / bucketQuota : 0;
    return dataPoint.set('attainmentPercentage', attainmentPercentage);
  });
  return {
    report,
    response: data.set('breakdowns', breakdownsWithAttainment).set('quotas', quotas)
  };
};

//remove owners which have no data present -> amount == 0
const removeZeroRows = (report, data) => {
  const bucketProperty = getBucketField(report);
  const amountField = getMetricField(report);
  const quotas = data.get('quotas').filter(point => point.get(`SUM|${amountField}`) !== 0 || point.get('SUM|count') !== 0);
  const bucketIds = ImmutableSet(quotas.map(point => point.get(bucketProperty)));
  const breakdowns = data.get('breakdowns').filter(point => bucketIds.has(point.get(bucketProperty)));
  return data.set('quotas', quotas).set('breakdowns', breakdowns);
};
export const filterCleanData = responseObject => {
  const {
    report,
    response: data
  } = sortData(responseObject);
  let adjustedData = data;
  if (!isTimeSeries(report) && !isTable(report)) {
    adjustedData = removeZeroRows(report, adjustedData);
  }
  return addAttainmentPercentage({
    report,
    response: adjustedData
  });
};
export const getTableData = ({
  report,
  response
}) => {
  const bucketField = getBucketField(report);
  const breakdownField = getBreakdownField(report);
  const amountField = `SUM|${getMetricField(report)}`;
  const countField = 'SUM|count';
  const quotas = response.get('quotas');
  if (!breakdownField) {
    return quotas;
  }
  const breakdowns = response.get('breakdowns').groupBy(data => data.get(bucketField));
  const quotaMap = mapQuotaToBucketProperty(quotas, bucketField);
  const tableData = breakdowns.reduce((acc, dataValue, dataKey) => {
    const forecastValues = getCombinedValues(dataValue, breakdownField, amountField, countField);
    const revenueAmount = quotaMap.getIn([dataKey, 'quotaAmount'], 0);
    return acc.push(forecastValues.set('quotaAmount', revenueAmount).set(bucketField, dataKey));
  }, List());
  return tableData;
};